import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import {
  FACEBOOK_CONTENT_CATEGORIES,
  FACEBOOK_CONTENT_CATEGORY_NONE,
  FACEBOOK_PAGE_REEL,
  FACEBOOK_PAGE_VIDEO,
  FACEBOOK_PUBLISH_TARGETS,
  FacebookDestinationPlatformData,
  FacebookPublicationSettings,
  isFacebookReelPublishTarget,
  normalizeFacebookPublicationSettings,
  resolveFacebookPublicationSettings,
} from "app/js/src/domain/destinations";
import {
  PublishBlocker,
  PublishStateService,
} from "app/js/src/infrastructure/clipping-studio/publish-state.service";
import { Subscription } from "rxjs";

type FacebookPublishForm = {
  publishTarget: FormControl<string | null>;
  embeddable: FormControl<boolean>;
  noStory: FormControl<boolean>;
  contentCategory: FormControl<string>;
};

@Component({
  selector: "evrideo-clipping-studio-facebook-publish-settings",
  templateUrl: "./clipping-studio-facebook-publish-settings.component.html",
  styleUrls: ["./clipping-studio-facebook-publish-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClippingStudioFacebookPublishSettingsComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) destinationUid!: string;
  @Input({ required: true }) destinationName!: string;
  @Input() destinationDefaults: FacebookDestinationPlatformData | null = null;
  @Input() publicationSettings: FacebookPublicationSettings | null = null;
  @Input() readonly = false;

  @Output() platformDataChange = new EventEmitter<FacebookPublicationSettings>();

  readonly facebookPublishTargets = FACEBOOK_PUBLISH_TARGETS;
  readonly facebookContentCategories = FACEBOOK_CONTENT_CATEGORIES;
  readonly facebookContentCategoryNone = FACEBOOK_CONTENT_CATEGORY_NONE;

  readonly form = new FormGroup<FacebookPublishForm>({
    publishTarget: new FormControl<string | null>(FACEBOOK_PAGE_VIDEO),
    embeddable: new FormControl<boolean>(false, { nonNullable: true }),
    noStory: new FormControl<boolean>(false, { nonNullable: true }),
    contentCategory: new FormControl<string>(FACEBOOK_CONTENT_CATEGORY_NONE, {
      nonNullable: true,
    }),
  });

  private readonly subscription = new Subscription();
  private syncing = false;

  constructor(
    private readonly publishState: PublishStateService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.subscription.add(
      this.form.valueChanges.subscribe(() => this.onFormChanged()),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["destinationDefaults"] ||
      changes["publicationSettings"] ||
      changes["destinationUid"] ||
      changes["destinationName"] ||
      changes["readonly"]
    ) {
      this.syncFromInputs();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get reelsEligible(): boolean {
    return this.destinationDefaults?.reels_eligible === true;
  }

  get isPageVideoTarget(): boolean {
    return !isFacebookReelPublishTarget(this.form.controls.publishTarget.value);
  }

  get contentCategorySelection(): string {
    return this.form.controls.contentCategory.value;
  }

  onPublishTargetChange(value: string): void {
    if (this.readonly || !this.isPublishTargetSelectable(value)) {
      this.changeDetectorRef.markForCheck();
      return;
    }
    this.form.controls.publishTarget.setValue(value);
  }

  onContentCategoryChange(value: string): void {
    if (this.readonly) {
      return;
    }
    this.form.controls.contentCategory.setValue(value);
  }

  isPublishTargetSelectable(value: string): boolean {
    if (value !== FACEBOOK_PAGE_REEL) {
      return true;
    }
    return this.reelsEligible;
  }

  private syncFromInputs(): void {
    this.syncing = true;
    const resolved = resolveFacebookPublicationSettings(
      this.publicationSettings,
      this.destinationDefaults,
    );
    const contentCategory =
      resolved.content_category ?? FACEBOOK_CONTENT_CATEGORY_NONE;

    this.form.reset(
      {
        publishTarget: resolved.publish_target ?? FACEBOOK_PAGE_VIDEO,
        embeddable: resolved.embeddable ?? false,
        noStory: resolved.no_story ?? false,
        contentCategory,
      },
      { emitEvent: false },
    );

    if (this.readonly) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }

    this.syncing = false;
    this.updatePublishGateFromForm();
    this.changeDetectorRef.markForCheck();
  }

  private onFormChanged(): void {
    if (this.syncing) {
      return;
    }
    const payload = this.toPublicationSettings();
    this.platformDataChange.emit(payload);
    this.updatePublishGateFromForm();
    this.changeDetectorRef.markForCheck();
  }

  private updatePublishGateFromForm(): void {
    this.publishState.setPublishBlockers(this.blockerSource, this.collectBlockers());
  }

  private toPublicationSettings(): FacebookPublicationSettings {
    const raw = this.form.getRawValue();
    const contentCategory =
      raw.contentCategory === FACEBOOK_CONTENT_CATEGORY_NONE
        ? null
        : raw.contentCategory;
    return normalizeFacebookPublicationSettings({
      publish_target: raw.publishTarget,
      embeddable: raw.embeddable,
      no_story: raw.noStory,
      content_category: contentCategory,
    });
  }

  private collectBlockers(): PublishBlocker[] {
    const blockers: PublishBlocker[] = [];
    const publishTarget = this.form.controls.publishTarget.value;
    if (
      isFacebookReelPublishTarget(publishTarget) &&
      !this.reelsEligible
    ) {
      blockers.push({
        code: "facebook_reel_not_eligible",
        message: `${this.destinationName} is not eligible for Page Reel uploads.`,
      });
    }
    return blockers;
  }

  private get blockerSource(): string {
    return `facebook:${this.destinationUid}`;
  }
}
